import { cloneElement, useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/utils/cn";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TextButton } from "./text-button";

interface ConfirmModalProps {
  // trigger 模式
  trigger?: React.ReactElement;
  // open 参数模式
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // 通用属性
  title: string;
  description: string;
  cancel?: string;
  confirm?: string;
  loading?: boolean;
  overlayClassName?: string;
  neverClose?: boolean;
  onCancel?: () => void;
  onConfirm: () => void | Promise<void>;
}

export const ConfirmModal = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  onConfirm,
  onCancel,
  title,
  description,
  cancel,
  confirm,
  overlayClassName,
  neverClose,
  loading = false,
}: ConfirmModalProps) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : (externalOpen ?? false);

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (neverClose) return;
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 在 open 参数模式下，同步外部 open 状态到内部状态
  useEffect(() => {
    if (!isTriggerMode && externalOpen !== undefined) {
      setInternalOpen(externalOpen);
    }
  }, [externalOpen, isTriggerMode]);

  const handleConfirm = async () => {
    // 判断 onConfirm 是否是异步函数，仅检查async/await
    if (onConfirm.constructor.name === "AsyncFunction") {
      await onConfirm();
    } else {
      onConfirm();
    }
    if (neverClose) return;
    handleOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      handleOpenChange(false);
    }
  };

  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const handleCancel = () => {
    onCancel?.();
    if (neverClose) return;
    handleOpenChange(false)
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50", overlayClassName)} />
      <Dialog.Content
        className={cn(
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-xl shadow-xl",
          "w-[400px] max-w-[90vw]",
          "py-4 px-6",
          "z-50"
        )}
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {/* 图标 */}
        <div className={cn("flex items-center justify-between mb-3")}>
          {/* 标题 */}
          <Dialog.Title
            className={cn("text-base font-semibold text-i-normal")}
          >
            {title}
          </Dialog.Title>
          {/* 关闭按钮 */}
          {!neverClose && <Dialog.Close asChild>
            <button
              className={cn(
                "flex items-center justify-center w-10 h-10",
                "text-gray-400 hover:text-gray-600 outline-gray-400",
                "transition-colors duration-200 focus:outline-none"
              )}
              onClick={() => handleOpenChange(false)}
            >
              <X className={cn("w-4 h-4")} />
            </button>
          </Dialog.Close>}
        </div>

        {/* 描述 */}
        <Dialog.Description
          className={cn("text-[rgba(71,84,103,1)] mb-6 text-sm")}
          dangerouslySetInnerHTML={{ __html: description }}
        >
        </Dialog.Description>

        {/* 按钮组 */}
        <div className={cn("flex gap-3 mb-2")}>
          <button
            onClick={handleCancel}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-black font-semibold",
              "border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-50 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-200"
            )}
          >
            {cancel || t("confirm_modal.cancel")}
          </button>
          <TextButton
            label={confirm || t('common.confirm')}
            onClick={() => handleConfirm()}
            loading={loading}
            disabled={loading}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-white font-semibold",
              "bg-i-normal rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-800 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-400",
            )}
          >
          </TextButton>
          {/* <button
            onClick={() => handleConfirm()}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-white font-semibold",
              "bg-i-normal rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-800 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-400"
            )}
          >
            {confirm || t("confirm_modal.confirm")}
          </button> */}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};
